import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const TakedownPolicy = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    return () => {
      if (badge) badge.style.display = 'flex';
    };
  }, []);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="pt-[env(safe-area-inset-top,12px)]" />
      
      <header className={`sticky top-0 border-b z-50 ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            <ArrowLeft className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <div>
            <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Takedown Policy</h1>
          </div>
        </div>
      </header>

      <div className="px-6 py-6">
        <div className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}>
          <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>SmartReadGo Publisher / Content Takedown Policy</h2>
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}><strong>Effective Date:</strong> 03 January 2026</p>
          
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            SmartReadGo respects intellectual property rights and publisher controls. This Publisher / Content Takedown Policy explains how rights holders can request removal, correction, or changes related to content displayed or stored in SmartReadGo.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>1) Who can submit a request</h3>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>You may submit a request if you are:</p>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2">the publisher, author, or copyright owner of the content, or</li>
            <li className="mb-2">an authorized agent representing the rights holder</li>
          </ul>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>2) What kinds of requests we handle</h3>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2">Remove content alleged to infringe copyright or other rights</li>
            <li className="mb-2">Correct publisher attribution (name/logo/source)</li>
            <li className="mb-2">Disable access to content you do not want displayed, indexed, summarized, or converted to audio</li>
            <li className="mb-2">Address unlawful content concerns (defamation, privacy violations, etc.)</li>
          </ul>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>3) How to submit a takedown request</h3>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Send an email to <a href="mailto:Admin@smartreadgo.com" className="text-blue-600 hover:underline">Admin@smartreadgo.com</a> with subject line: \"Content Takedown Request – SmartReadGo\"</p>
          
          <p className={`mb-2 mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}><strong>Include all of the following:</strong></p>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2">Your full name and organization (if any)</li>
            <li className="mb-2">Proof of ownership/authority</li>
            <li className="mb-2">Exact content identifiers (article title, publisher/source name, screenshot)</li>
            <li className="mb-2">The reason for the request</li>
            <li className="mb-2">The action you want (remove, disable audio, fix attribution, etc.)</li>
            <li className="mb-2">A good-faith statement that the information is accurate</li>
          </ul>

          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            You may also contact us on WhatsApp <a href="https://wa.me/919004685736" className="text-blue-600 hover:underline">+91 9004685736</a>, but email is required for formal processing.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>4) What happens after you submit</h3>\n          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>\n            We will acknowledge receipt and may request additional information. We aim to review and act within 3–7 business days, depending on complexity.\n          </p>\n\n          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>7) Contact</h3>\n          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>SmartReadGo (Proprietorship)</p>\n          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mumbai, Maharashtra, India</p>\n          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email: <a href="mailto:Admin@smartreadgo.com" className="text-blue-600 hover:underline">Admin@smartreadgo.com</a></p>\n          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>WhatsApp: <a href="https://wa.me/919004685736" className="text-blue-600 hover:underline">+91 9004685736</a></p>\n\n          <div className="h-20" />
        </div>
      </div>
    </div>
  );
};

export default TakedownPolicy;
